export const SUPERADMIN_ROUTE_SEGMENTS = {
  ROOT: 'superadmin',
  DASHBOARD: 'dashboard',
  ERROR_LOGS: 'error-logs',
  APPLICATION_SETTINGS: 'application-settings',
  LOGIN_PAGE_SETTINGS: 'login-page',
  UI_SETTINGS: 'ui',
  INSTRUCTION_MANAGER: 'instruction-manager',
  EMAIL_TEMPLATES: 'email-templates',
  LEGAL_PAGES: 'legal-pages',
  NOTIFICATION_ENGINE: 'notification-engine',
  USERS: 'users',
  USER_SUMMARY: 'summary',
  USER_EDIT: 'edit',
  AUDIT_REPORT: 'audit-report',
  APPLICATION_PERMISSIONS: 'application-permissions',
  PERMISSION_MATRIX: 'permissions-matrix',
  PERMISSION_AUDIT: 'audit/permissions',
  ROLE_COMPARISON: 'role-comparison',
  ROLE_TEMPLATES: 'role-templates',
  ROLE_TEMPLATES_CREATE: 'create',
  HOLIDAY_CALENDARS: 'holiday-calendars',
  BUSINESS_HOURS: 'business-hours',
  MASTER_DATA: 'master-data',
  CONFIGURATION: 'configuration',
  // Program Governance
  PROGRAM_GOVERNANCE: 'program-governance',
  DASHBOARD_GOVERNANCE: 'dashboard-governance',
  // Platform Governance
  TENANTS: 'tenants',
  ENVIRONMENTS: 'environments',
  RELEASES: 'releases',
  FEATURE_FLAGS: 'feature-flags',
  // Observability
  PLATFORM_OPS: 'platform-ops',
  SYSTEM_HEALTH: 'system-health',
  SYSTEM_ALERTS: 'system-alerts',
  JOB_MONITOR: 'job-monitor',
  // Program Request Portal
  PROGRAM_REQUESTS: 'program-requests',
  // Program Setup Assistant
  PROGRAM_SETUP: 'program-setup',
  // Program Configuration Studio
  PROGRAM_CONFIGURATION_STUDIO: 'program-configuration-studio',
  // User Guidance
  USER_GUIDANCE: 'user-guidance',
} as const;

export const SUPERADMIN_ROUTES = {
  ROOT: '/superadmin',
  DASHBOARD: '/superadmin/dashboard',
  ERROR_LOGS: '/superadmin/error-logs',
  APPLICATION_SETTINGS: '/superadmin/application-settings',
  APPLICATION_SETTINGS_LOGIN_PAGE: '/superadmin/application-settings/login-page',
  APPLICATION_SETTINGS_UI: '/superadmin/application-settings/ui',
  APPLICATION_SETTINGS_INSTRUCTION_MANAGER: '/superadmin/application-settings/instruction-manager',
  APPLICATION_SETTINGS_EMAIL_TEMPLATES: '/superadmin/application-settings/email-templates',
  APPLICATION_SETTINGS_LEGAL_PAGES: '/superadmin/application-settings/legal-pages',
  APPLICATION_SETTINGS_NOTIFICATION_ENGINE: '/superadmin/application-settings/notification-engine',
  USERS: '/superadmin/users',
  USER_SUMMARY: (userId: string) => `/superadmin/users/${userId}/summary`,
  USER_EDIT: (userId: string) => `/superadmin/users/${userId}/edit`,
  USER_DETAIL: (userId: string) => `/superadmin/users/${userId}`,
  AUDIT_REPORT: '/superadmin/audit-report',
  APPLICATION_PERMISSIONS: '/superadmin/application-permissions',
  PERMISSION_MATRIX: '/superadmin/permissions-matrix',
  PERMISSION_AUDIT: '/superadmin/audit/permissions',
  ROLE_COMPARISON: '/superadmin/role-comparison',
  ROLE_TEMPLATES: '/superadmin/role-templates',
  ROLE_TEMPLATE_CREATE: '/superadmin/role-templates/create',
  ROLE_TEMPLATE_DETAIL: (templateId: string) => `/superadmin/role-templates/${templateId}`,
  ROLE_TEMPLATE_EDIT: (templateId: string) => `/superadmin/role-templates/${templateId}/edit`,
  HOLIDAY_CALENDARS: '/superadmin/holiday-calendars',
  BUSINESS_HOURS: '/superadmin/business-hours',
  MASTER_DATA: '/superadmin/master-data',
  CONFIGURATION: '/superadmin/configuration',
  // Program Governance
  PROGRAM_GOVERNANCE: '/superadmin/program-governance',
  DASHBOARD_GOVERNANCE: '/superadmin/dashboard-governance',
  // Platform Governance
  TENANTS: '/superadmin/tenants',
  ENVIRONMENTS: '/superadmin/environments',
  RELEASES: '/superadmin/releases',
  FEATURE_FLAGS: '/superadmin/feature-flags',
  // Observability
  PLATFORM_OPS: '/superadmin/platform-ops',
  SYSTEM_HEALTH: '/superadmin/system-health',
  SYSTEM_ALERTS: '/superadmin/system-alerts',
  JOB_MONITOR: '/superadmin/job-monitor',
  // Program Request Portal
  PROGRAM_REQUESTS: '/superadmin/program-requests',
  PROGRAM_REQUEST_DETAIL: (requestId: string) => `/superadmin/program-requests/${requestId}`,
  // Program Setup Assistant
  PROGRAM_SETUP: '/superadmin/program-setup',
  PROGRAM_SETUP_NEW: '/superadmin/program-setup/new',
  PROGRAM_SETUP_PLAN: (planId: string) => `/superadmin/program-setup/plans/${planId}`,
  PROGRAM_SETUP_EXECUTION: (planId: string) => `/superadmin/program-setup/plans/${planId}/execution`,
  PROGRAM_SETUP_HISTORY: '/superadmin/program-setup/history',
  // Program Configuration Studio
  PROGRAM_CONFIGURATION_STUDIO: '/superadmin/program-configuration-studio',
  PROGRAM_CONFIGURATION_STUDIO_NEW: '/superadmin/program-configuration-studio/new',
  PROGRAM_CONFIGURATION_STUDIO_DETAIL: (importId: string) => `/superadmin/program-configuration-studio/${importId}`,
  // User Guidance
  USER_GUIDANCE: '/superadmin/user-guidance',
} as const;

export const SUPERADMIN_ROUTE_LABELS: Record<string, string> = {
  [SUPERADMIN_ROUTES.DASHBOARD]: 'Dashboard',
  [SUPERADMIN_ROUTES.ERROR_LOGS]: 'Error Logs',
  [SUPERADMIN_ROUTES.APPLICATION_SETTINGS]: 'Application Settings',
  [SUPERADMIN_ROUTES.APPLICATION_PERMISSIONS]: 'Application Permissions',
  [SUPERADMIN_ROUTES.PERMISSION_MATRIX]: 'Permission Matrix',
  [SUPERADMIN_ROUTES.PERMISSION_AUDIT]: 'Permission Audit',
  [SUPERADMIN_ROUTES.ROLE_COMPARISON]: 'Role Comparison',
  [SUPERADMIN_ROUTES.ROLE_TEMPLATES]: 'Role Templates',
  [SUPERADMIN_ROUTES.AUDIT_REPORT]: 'Audit Report',
  [SUPERADMIN_ROUTES.USERS]: 'Users',
  [SUPERADMIN_ROUTES.HOLIDAY_CALENDARS]: 'Holiday Calendars',
  [SUPERADMIN_ROUTES.BUSINESS_HOURS]: 'Business Hours',
  [SUPERADMIN_ROUTES.MASTER_DATA]: 'Master Data',
  [SUPERADMIN_ROUTES.CONFIGURATION]: 'Configuration',
  // Program Governance
  [SUPERADMIN_ROUTES.PROGRAM_GOVERNANCE]: 'Program Governance',
  [SUPERADMIN_ROUTES.DASHBOARD_GOVERNANCE]: 'Dashboard Governance',
  // Platform Governance
  [SUPERADMIN_ROUTES.TENANTS]: 'Tenant Management',
  [SUPERADMIN_ROUTES.ENVIRONMENTS]: 'Environments',
  [SUPERADMIN_ROUTES.RELEASES]: 'Release Management',
  [SUPERADMIN_ROUTES.FEATURE_FLAGS]: 'Feature Flags',
  // Observability
  [SUPERADMIN_ROUTES.PLATFORM_OPS]: 'Platform Operations',
  [SUPERADMIN_ROUTES.SYSTEM_HEALTH]: 'System Health',
  [SUPERADMIN_ROUTES.SYSTEM_ALERTS]: 'System Alerts',
  [SUPERADMIN_ROUTES.JOB_MONITOR]: 'Job Monitor',
  [SUPERADMIN_ROUTES.PROGRAM_CONFIGURATION_STUDIO]: 'Program Configuration Studio',
  [SUPERADMIN_ROUTES.USER_GUIDANCE]: 'User Guidance',
};